/**
 * Voice Subscription Hooks
 *
 * Follows the same useState/useEffect/useCallback pattern as usePricing.ts.
 */

'use client';

import { useState, useEffect, useCallback } from 'react';
import {
  getVoiceSubscription,
  getVoiceMinutes,
  getVoiceSuccessFeeSummary,
  getVoiceUsageSummary,
  getVoiceServiceCatalog,
  startVoiceTrial,
  changeVoiceTier,
  activateVoiceSubscription,
  cancelVoiceSubscription,
  reactivateVoiceSubscription,
  setVoiceVertical,
  setVoiceOutcomesTrack,
} from '@/services/voiceSubscriptionApi';
import type {
  VoiceSubscription,
  VoiceMinutesRemaining,
  VoiceSuccessFeeSummary,
  VoiceUsageSummary,
  VoiceServiceCatalogItem,
  VoiceTier,
  VoiceVertical,
  VoiceOutcomeBand,
  VoiceBillingCycle,
} from '@/types/voice-subscription';

// ── useVoiceSubscription ───────────────────────────────────────────────────

interface UseVoiceSubscriptionReturn {
  subscription: VoiceSubscription | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
  startTrial: () => Promise<void>;
  changeTier: (newTier: VoiceTier, billingCycle?: VoiceBillingCycle, effectiveImmediately?: boolean) => Promise<void>;
  activate: (tier: VoiceTier) => Promise<void>;
  cancel: () => Promise<void>;
  reactivate: (newTier: VoiceTier) => Promise<void>;
  setVertical: (vertical: VoiceVertical | null, outcomeBand?: VoiceOutcomeBand | null) => Promise<void>;
  setOutcomesTrack: (enabled: boolean) => Promise<void>;
  isMutating: boolean;
}

export function useVoiceSubscription(tenantId: number | null): UseVoiceSubscriptionReturn {
  const [subscription, setSubscription] = useState<VoiceSubscription | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isMutating, setIsMutating] = useState(false);

  const fetchData = useCallback(async () => {
    if (!tenantId) {
      setIsLoading(false);
      setSubscription(null);
      setError(null);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const result = await getVoiceSubscription(tenantId);
      setSubscription(result.subscription ?? null);
      // No subscription yet is expected for first-time users — not an error
      if (!result.subscription) {
        setError(null);
      }
    } catch (err: any) {
      setSubscription(null);
      setError(err?.message ?? 'Failed to load voice subscription');
    } finally {
      setIsLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const startTrial = useCallback(async () => {
    if (!tenantId) return;
    setIsMutating(true);
    try {
      const result = await startVoiceTrial(tenantId);
      if (result.subscription) setSubscription(result.subscription);
    } finally {
      setIsMutating(false);
    }
  }, [tenantId]);

  const changeTier = useCallback(
    async (newTier: VoiceTier, billingCycle?: VoiceBillingCycle, effectiveImmediately?: boolean) => {
      if (!tenantId) return;
      setIsMutating(true);
      try {
        const result = await changeVoiceTier({
          tenant_id: tenantId,
          new_tier: newTier,
          billing_cycle: billingCycle,
          effective_immediately: effectiveImmediately,
        });
        if (result.subscription) setSubscription(result.subscription);
      } finally {
        setIsMutating(false);
      }
    },
    [tenantId]
  );

  const activate = useCallback(
    async (tier: VoiceTier) => {
      if (!tenantId) return;
      setIsMutating(true);
      try {
        const periodStart = new Date();
        const periodEnd = new Date(periodStart);
        periodEnd.setMonth(periodEnd.getMonth() + 1);
        const result = await activateVoiceSubscription({
          tenant_id: tenantId,
          tier,
          // TODO(voice-payments): placeholder Stripe IDs — voice has no card-collection
          // step yet. Port the SaaS pattern (account/subscription/page.tsx:
          // tokenize card → PaymentAPI.createStripeSubscription) before this goes live.
          stripe_customer_id: `cus_placeholder_${tenantId}`,
          stripe_subscription_id: `sub_placeholder_${tenantId}_${periodStart.getTime()}`,
          billing_period_start: periodStart.toISOString(),
          billing_period_end: periodEnd.toISOString(),
        });
        if (result.subscription) setSubscription(result.subscription);
      } finally {
        setIsMutating(false);
      }
    },
    [tenantId]
  );

  const cancel = useCallback(async () => {
    if (!tenantId) return;
    setIsMutating(true);
    try {
      const result = await cancelVoiceSubscription(tenantId);
      if (result.subscription) setSubscription(result.subscription);
    } finally {
      setIsMutating(false);
    }
  }, [tenantId]);

  const reactivate = useCallback(
    async (newTier: VoiceTier) => {
      if (!tenantId) return;
      setIsMutating(true);
      try {
        const result = await reactivateVoiceSubscription(tenantId, newTier);
        if (result.subscription) setSubscription(result.subscription);
      } finally {
        setIsMutating(false);
      }
    },
    [tenantId]
  );

  const setVertical = useCallback(
    async (vertical: VoiceVertical | null, outcomeBand?: VoiceOutcomeBand | null) => {
      if (!tenantId) return;
      setIsMutating(true);
      try {
        const result = await setVoiceVertical(tenantId, vertical, outcomeBand);
        if (result.subscription) setSubscription(result.subscription);
      } finally {
        setIsMutating(false);
      }
    },
    [tenantId]
  );

  const setOutcomesTrack = useCallback(
    async (enabled: boolean) => {
      if (!tenantId) return;
      setIsMutating(true);
      try {
        const result = await setVoiceOutcomesTrack(tenantId, enabled);
        if (result.subscription) setSubscription(result.subscription);
      } finally {
        setIsMutating(false);
      }
    },
    [tenantId]
  );

  return {
    subscription,
    isLoading,
    error,
    refetch: fetchData,
    startTrial,
    changeTier,
    activate,
    cancel,
    reactivate,
    setVertical,
    setOutcomesTrack,
    isMutating,
  };
}

// ── useVoiceMinutes ────────────────────────────────────────────────────────

export function useVoiceMinutes(tenantId: number | null) {
  const [data, setData] = useState<VoiceMinutesRemaining | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    if (!tenantId) return;
    setIsLoading(true);
    try {
      const result = await getVoiceMinutes(tenantId);
      setData(result);
      setError(null);
    } catch (err: any) {
      const msg = err?.message ?? '';
      if (/no voice subscription/i.test(msg)) {
        setData(null);
        setError(null);
      } else {
        setError(msg || 'Failed to load minutes');
      }
    } finally {
      setIsLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return { data, isLoading, error, refetch: fetch };
}

// ── useVoiceUsageSummary ───────────────────────────────────────────────────

export function useVoiceUsageSummary(tenantId: number | null, billingPeriod?: string) {
  const [data, setData] = useState<VoiceUsageSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    if (!tenantId) return;
    setIsLoading(true);
    try {
      const result = await getVoiceUsageSummary(tenantId, billingPeriod);
      setData(result);
      setError(null);
    } catch (err: any) {
      const msg = err?.message ?? '';
      if (/no voice subscription/i.test(msg)) {
        setData(null);
        setError(null);
      } else {
        setError(msg || 'Failed to load usage summary');
      }
    } finally {
      setIsLoading(false);
    }
  }, [tenantId, billingPeriod]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return { data, isLoading, error, refetch: fetch };
}

// ── useVoiceServiceCatalog ─────────────────────────────────────────────────

export function useVoiceServiceCatalog(tenantId: number | null) {
  const [data, setData] = useState<VoiceServiceCatalogItem[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    if (!tenantId) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      const result = await getVoiceServiceCatalog(tenantId);
      setData(result.services ?? null);
      setError(null);
    } catch (err: any) {
      setError(err?.message ?? 'Failed to load service catalog');
    } finally {
      setIsLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return { data, isLoading, error, refetch: fetch };
}

// ── useVoiceSuccessFeeSummary ──────────────────────────────────────────────

export function useVoiceSuccessFeeSummary(
  tenantId: number | null,
  billingPeriod?: string
) {
  const [data, setData] = useState<VoiceSuccessFeeSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    if (!tenantId) return;
    setIsLoading(true);
    try {
      const result = await getVoiceSuccessFeeSummary(tenantId, billingPeriod);
      setData(result);
      setError(null);
    } catch (err: any) {
      const msg = err?.message ?? '';
      if (/no voice subscription/i.test(msg)) {
        setData(null);
        setError(null);
      } else {
        setError(msg || 'Failed to load success fee summary');
      }
    } finally {
      setIsLoading(false);
    }
  }, [tenantId, billingPeriod]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return { data, isLoading, error, refetch: fetch };
}
